/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod";
import { ClosedEnum } from "../types/enums.js";

/**
 * 节日筛选类型，默认 all。
 */
export const HolidayType = {
  All: "all",
  Legal: "legal",
  LegalRest: "legal_rest",
  LegalWorkday: "legal_workday",
  Solar: "solar",
  Lunar: "lunar",
  Term: "term",
} as const;
/**
 * 节日筛选类型，默认 all。
 */
export type HolidayType = ClosedEnum<typeof HolidayType>;

export const HolidayType$zodSchema = z.enum([
  "all",
  "legal",
  "legal_rest",
  "legal_workday",
  "solar",
  "lunar",
  "term",
]).describe("节日筛选类型，默认 all。");

export type GetMiscHolidayCalendarRequest = {
  date?: string | undefined;
  month?: string | undefined;
  year?: string | undefined;
  timezone?: string | undefined;
  holiday_type?: HolidayType | undefined;
  include_nearby?: boolean | undefined;
  nearby_limit?: number | undefined;
};

export const GetMiscHolidayCalendarRequest$zodSchema: z.ZodType<
  GetMiscHolidayCalendarRequest
> = z.object({
  date: z.string().describe(
    "按天查询时填写这个参数，例如查某一天。格式：`YYYY-MM-DD`。和 `month`、`year` 三选一。",
  ).optional(),
  holiday_type: HolidayType$zodSchema.default("all"),
  include_nearby: z.boolean().default(false).describe(
    "是否返回前后最近节日，仅 date 模式生效，默认 false。month/year 模式会忽略此参数。",
  ),
  month: z.string().describe(
    "按月查询时填写这个参数，例如查某个月。格式：`YYYY-MM`。和 `date`、`year` 三选一。",
  ).optional(),
  nearby_limit: z.int().default(7).describe(
    "返回最近节日数量限制，默认 7，最大 30。仅 date 模式 + include_nearby=true 生效。",
  ),
  timezone: z.string().default("Asia/Shanghai").describe(
    "时区名称，默认 Asia/Shanghai。",
  ),
  year: z.string().describe(
    "按年查询时填写这个参数，例如查某一年。格式：`YYYY`。和 `date`、`month` 三选一。",
  ).optional(),
});

/**
 * 请求参数错误。常见原因：
 *
 * @remarks
 * - `date`、`month`、`year` 未传或同时传入多个
 * - 日期格式错误：`date` 必须为 `YYYY-MM-DD`、`month` 必须为 `YYYY-MM`、`year` 必须为 `YYYY`
 * - `holiday_type` 非法
 * - `timezone` 非法
 */
export type GetMiscHolidayCalendarBadRequestResponseBody = {
  code?: number | undefined;
  message?: string | undefined;
};

export const GetMiscHolidayCalendarBadRequestResponseBody$zodSchema: z.ZodType<
  GetMiscHolidayCalendarBadRequestResponseBody
> = z.object({
  code: z.int().optional(),
  message: z.string().optional(),
}).describe(
  "请求参数错误。常见原因：\n- `date`、`month`、`year` 未传或同时传入多个\n- 日期格式错误：`date` 必须为 `YYYY-MM-DD`、`month` 必须为 `YYYY-MM`、`year` 必须为 `YYYY`\n- `holiday_type` 非法\n- `timezone` 非法",
);

/**
 * 请求参数回显。
 */
export type Query = {
  date?: string | undefined;
  month?: string | undefined;
  year?: string | undefined;
  timezone?: string | undefined;
  holiday_type?: string | undefined;
  include_nearby?: boolean | undefined;
  nearby_limit?: number | undefined;
};

export const Query$zodSchema: z.ZodType<Query> = z.object({
  date: z.string().optional(),
  holiday_type: z.string().optional(),
  include_nearby: z.boolean().optional(),
  month: z.string().optional(),
  nearby_limit: z.int().optional(),
  timezone: z.string().optional(),
  year: z.string().optional(),
}).describe("请求参数回显。");

/**
 * 统计摘要。
 */
export type Summary = {
  total_days?: number | undefined;
  weekend_days?: number | undefined;
  workdays?: number | undefined;
  rest_days?: number | undefined;
  holiday_events?: number | undefined;
  legal_rest_days?: number | undefined;
  legal_workdays?: number | undefined;
};

export const Summary$zodSchema: z.ZodType<Summary> = z.object({
  holiday_events: z.int().optional(),
  legal_rest_days: z.int().optional(),
  legal_workdays: z.int().optional(),
  rest_days: z.int().optional(),
  total_days: z.int().optional(),
  weekend_days: z.int().optional(),
  workdays: z.int().optional(),
}).describe("统计摘要。");

export type Day = {
  date?: string | undefined;
  year?: number | undefined;
  month?: number | undefined;
  day?: number | undefined;
  weekday_cn?: string | undefined;
  is_weekend?: boolean | undefined;
  is_workday?: boolean | undefined;
  is_rest_day?: boolean | undefined;
  is_holiday?: boolean | undefined;
  legal_holiday_name?: string | undefined;
  legal_holiday_type?: string | undefined;
  solar_festival?: string | undefined;
  lunar_festival?: string | undefined;
  solar_term?: string | undefined;
  lunar_year?: number | undefined;
  lunar_month?: number | undefined;
  lunar_day?: number | undefined;
  lunar_month_name?: string | undefined;
  lunar_day_name?: string | undefined;
  ganzhi_year?: string | undefined;
  ganzhi_month?: string | undefined;
  ganzhi_day?: string | undefined;
};

export const Day$zodSchema: z.ZodType<Day> = z.object({
  date: z.string().optional(),
  day: z.int().optional(),
  ganzhi_day: z.string().optional(),
  ganzhi_month: z.string().optional(),
  ganzhi_year: z.string().optional(),
  is_holiday: z.boolean().optional(),
  is_rest_day: z.boolean().optional(),
  is_weekend: z.boolean().optional(),
  is_workday: z.boolean().optional(),
  legal_holiday_name: z.string().optional(),
  legal_holiday_type: z.string().optional(),
  lunar_day: z.int().optional(),
  lunar_day_name: z.string().optional(),
  lunar_festival: z.string().optional(),
  lunar_month: z.int().optional(),
  lunar_month_name: z.string().optional(),
  lunar_year: z.int().optional(),
  month: z.int().optional(),
  solar_festival: z.string().optional(),
  solar_term: z.string().optional(),
  weekday_cn: z.string().optional(),
  year: z.int().optional(),
});

/**
 * 事件类型。
 */
export const GetMiscHolidayCalendarType = {
  LegalRest: "legal_rest",
  LegalWorkdayAdjust: "legal_workday_adjust",
  SolarFestival: "solar_festival",
  LunarFestival: "lunar_festival",
  SolarTerm: "solar_term",
} as const;
/**
 * 事件类型。
 */
export type GetMiscHolidayCalendarType = ClosedEnum<
  typeof GetMiscHolidayCalendarType
>;

export const GetMiscHolidayCalendarType$zodSchema = z.enum([
  "legal_rest",
  "legal_workday_adjust",
  "solar_festival",
  "lunar_festival",
  "solar_term",
]).describe("事件类型。");

export type Holiday = {
  date?: string | undefined;
  name?: string | undefined;
  type?: GetMiscHolidayCalendarType | undefined;
  is_workday?: boolean | undefined;
};

export const Holiday$zodSchema: z.ZodType<Holiday> = z.object({
  date: z.string().optional(),
  is_workday: z.boolean().optional(),
  name: z.string().optional(),
  type: GetMiscHolidayCalendarType$zodSchema.optional(),
});

export type Previous = {
  date?: string | undefined;
  name?: string | undefined;
  type?: string | undefined;
};

export const Previous$zodSchema: z.ZodType<Previous> = z.object({
  date: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
});

export type Next = {
  date?: string | undefined;
  name?: string | undefined;
  type?: string | undefined;
};

export const Next$zodSchema: z.ZodType<Next> = z.object({
  date: z.string().optional(),
  name: z.string().optional(),
  type: z.string().optional(),
});

/**
 * 前后最近节日，仅 include_nearby=true 且 date 模式返回。
 */
export type Nearby = {
  previous?: Array<Previous> | undefined;
  next?: Array<Next> | undefined;
};

export const Nearby$zodSchema: z.ZodType<Nearby> = z.object({
  next: z.array(z.lazy(() => Next$zodSchema)).optional(),
  previous: z.array(z.lazy(() => Previous$zodSchema)).optional(),
}).describe("前后最近节日，仅 include_nearby=true 且 date 模式返回。");

export type GetMiscHolidayCalendarData = {
  mode?: string | undefined;
  query?: Query | undefined;
  summary?: Summary | undefined;
  days?: Array<Day> | undefined;
  holidays?: Array<Holiday> | undefined;
  nearby?: Nearby | undefined;
};

export const GetMiscHolidayCalendarData$zodSchema: z.ZodType<
  GetMiscHolidayCalendarData
> = z.object({
  days: z.array(z.lazy(() => Day$zodSchema)).optional(),
  holidays: z.array(z.lazy(() => Holiday$zodSchema)).optional(),
  mode: z.string().optional(),
  nearby: z.lazy(() => Nearby$zodSchema).optional(),
  query: z.lazy(() => Query$zodSchema).optional(),
  summary: z.lazy(() => Summary$zodSchema).optional(),
});

/**
 * 查询成功，返回指定范围的万年历与节假日信息。
 */
export type GetMiscHolidayCalendarResponseBody = {
  code?: number | undefined;
  message?: string | undefined;
  data?: GetMiscHolidayCalendarData | undefined;
};

export const GetMiscHolidayCalendarResponseBody$zodSchema: z.ZodType<
  GetMiscHolidayCalendarResponseBody
> = z.object({
  code: z.int().optional(),
  data: z.lazy(() => GetMiscHolidayCalendarData$zodSchema).optional(),
  message: z.string().optional(),
}).describe("查询成功，返回指定范围的万年历与节假日信息。");

export type GetMiscHolidayCalendarResponse =
  | GetMiscHolidayCalendarResponseBody
  | GetMiscHolidayCalendarBadRequestResponseBody;

export const GetMiscHolidayCalendarResponse$zodSchema: z.ZodType<
  GetMiscHolidayCalendarResponse
> = z.union([
  z.lazy(() => GetMiscHolidayCalendarResponseBody$zodSchema),
  z.lazy(() => GetMiscHolidayCalendarBadRequestResponseBody$zodSchema),
]);
